// client/src/components/pages/TelegramBI/Analytics/SystemHealthCard.js
import React from 'react';
import {
  FaServer,
  FaDatabase,
  FaCog,
  FaClock,
  FaRobot,
  FaMemory,
  FaCheckCircle,
  FaExclamationTriangle,
  FaTimesCircle,
  FaHeartbeat,
} from 'react-icons/fa';

const SERVICE_CONFIG = {
  database: { label: 'Database', icon: FaDatabase },
  queryEngine: { label: 'Query Engine', icon: FaCog },
  templateEngine: { label: 'Template Engine', icon: FaCog },
  scheduling: { label: 'Scheduling', icon: FaClock },
  bot: { label: 'Telegram Bot', icon: FaRobot },
  redis: { label: 'Redis Cache', icon: FaMemory },
};

const STATUS_STYLES = {
  healthy: {
    dot: 'bg-emerald-500',
    ping: 'bg-emerald-400',
    text: 'text-emerald-700',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    label: 'Healthy',
    icon: FaCheckCircle,
    iconColor: 'text-emerald-500',
  },
  degraded: {
    dot: 'bg-amber-500',
    ping: 'bg-amber-400',
    text: 'text-amber-700',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    label: 'Degraded',
    icon: FaExclamationTriangle,
    iconColor: 'text-amber-500',
  },
  unhealthy: {
    dot: 'bg-red-500',
    ping: 'bg-red-400',
    text: 'text-red-700',
    bg: 'bg-red-50',
    border: 'border-red-200',
    label: 'Unhealthy',
    icon: FaTimesCircle,
    iconColor: 'text-red-500',
  },
};

const getStatusStyle = (status) => {
  if (!status) return STATUS_STYLES.unhealthy;
  const normalized = status.toLowerCase();
  if (normalized === 'healthy' || normalized === 'ok' || normalized === 'connected')
    return STATUS_STYLES.healthy;
  if (normalized === 'degraded' || normalized === 'warning') return STATUS_STYLES.degraded;
  return STATUS_STYLES.unhealthy;
};

const StatusIndicator = ({ status, size = 'sm' }) => {
  const style = getStatusStyle(status);
  const dotSize = size === 'lg' ? 'h-3 w-3' : 'h-2 w-2';
  const pingSize = size === 'lg' ? 'h-3 w-3' : 'h-2 w-2';

  return (
    <span
      className="relative flex items-center justify-center"
      style={{ width: size === 'lg' ? 12 : 8, height: size === 'lg' ? 12 : 8 }}
    >
      {style === STATUS_STYLES.healthy && (
        <span
          className={`animate-ping absolute inline-flex rounded-full ${pingSize} ${style.ping} opacity-75`}
        />
      )}
      <span className={`relative inline-flex rounded-full ${dotSize} ${style.dot}`} />
    </span>
  );
};

const SystemHealthCard = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="bg-surface rounded-lg border border-gray-200 p-5">
        <div className="animate-pulse space-y-4">
          <div className="h-5 bg-gray-200 rounded w-32" />
          <div className="grid grid-cols-2 gap-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="h-14 bg-gray-100 rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-surface rounded-lg border border-gray-200">
        <div className="px-5 py-4 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <FaHeartbeat className="text-primary-500" />
            System Health
          </h3>
        </div>
        <div className="px-5 py-12 text-center">
          <FaServer className="mx-auto text-3xl text-gray-300 mb-3" />
          <p className="text-sm text-gray-500">Health data unavailable</p>
          <p className="text-xs text-gray-400 mt-1">Unable to retrieve system health status</p>
        </div>
      </div>
    );
  }

  const overallStatus = data.status || 'unhealthy';
  const services = data.services || {};
  const overallStyle = getStatusStyle(overallStatus);
  const OverallIcon = overallStyle.icon;

  // Build service entries from the services object
  const serviceEntries = Object.entries(SERVICE_CONFIG).map(([key, config]) => {
    const serviceStatus = services[key] || 'unknown';
    return {
      key,
      ...config,
      status: serviceStatus,
      style: getStatusStyle(serviceStatus),
    };
  });

  // Count healthy vs total
  const healthyCount = serviceEntries.filter(
    (s) => getStatusStyle(s.status) === STATUS_STYLES.healthy
  ).length;
  const totalServices = serviceEntries.length;

  const now = new Date();
  const lastCheckTime = now.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  return (
    <div className="bg-surface rounded-lg border border-gray-200">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <FaHeartbeat className="text-primary-500" />
          System Health
        </h3>
        <span className="text-[11px] text-gray-400">Last checked: {lastCheckTime}</span>
      </div>

      <div className="p-5">
        {/* Overall Status Banner */}
        <div
          className={`rounded-lg border px-4 py-3 mb-5 flex items-center justify-between ${overallStyle.bg} ${overallStyle.border}`}
        >
          <div className="flex items-center gap-3">
            <StatusIndicator status={overallStatus} size="lg" />
            <div>
              <div className={`text-sm font-semibold ${overallStyle.text}`}>
                System {overallStyle.label}
              </div>
              <div className="text-xs text-gray-500">
                {healthyCount}/{totalServices} services operational
              </div>
            </div>
          </div>
          <OverallIcon className={`text-lg ${overallStyle.iconColor}`} />
        </div>

        {/* Service Grid */}
        <div className="grid grid-cols-2 gap-2.5">
          {serviceEntries.map((service) => {
            const ServiceIcon = service.icon;
            return (
              <div
                key={service.key}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-gray-100 hover:border-gray-200 bg-gray-50/50 transition-colors"
              >
                <ServiceIcon className="text-xs text-gray-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-gray-700 truncate">{service.label}</div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <StatusIndicator status={service.status} />
                  <span className={`text-[11px] font-medium capitalize ${service.style.text}`}>
                    {service.style.label}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SystemHealthCard;
